import { DateTime } from 'luxon';

const getLocalizedTimestamp = (dateInput: number | undefined | null) => {
  const dateToParse = dateInput as number;
  const dateTimeObj = DateTime.fromSeconds(dateToParse);

  return dateTimeObj.toLocaleString({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    weekday: 'short',
    timeZoneName: 'short',
    // DateTime objects are created in the system's local zone so this will get user's local timezone
    timeZone: dateTimeObj.zoneName,
  });
};

/**
 * Formats a timestamp string to a localized date/time string for Source Review components.
 * @param timestamp - Timestamp string in milliseconds
 * @param showSeconds - Whether to include seconds in the output (default: true)
 * @param locale - Locale string for formatting (optional, defaults to 'en-US')
 * @returns Formatted date/time string or empty string if invalid
 */
const formatSourceReviewDateTime = (
  timestamp: string | null | undefined,
  showSeconds = true,
  locale?: string
): string => {
  if (!timestamp) return '';

  try {
    const timestampNum = parseInt(timestamp, 10);
    if (isNaN(timestampNum)) return '';

    const targetLocale = locale || 'en-US';
    const dateTimeObj =
      DateTime.fromMillis(timestampNum).setLocale(targetLocale);
    if (!dateTimeObj.isValid) return '';

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: dateTimeObj.zoneName,
      timeZoneName: 'short',
    };

    if (showSeconds) {
      options.second = '2-digit';
    }

    return dateTimeObj.toLocaleString(options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

export { getLocalizedTimestamp, formatSourceReviewDateTime };
